"use client"

import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { IoIosArrowDown as ArrowDown } from "react-icons/io";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import SearchDropdown from "../../../components/Dropdowns/SearchDropdown";
import OptionalOption from "../../../components/FilterComponents/OptionalOption";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { ContractToCloseStatusOptions } from "../../../utils/options";

const ContractCloseFilterData = ({ isSidebarOpen, filterData, onSetFilterData, onCallApiAgain, from }) => {
  const [config] = useAuth()
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [currentPhaseOptions, setCurrentPhaseOptions] = useState([])
  const [nextActionOptions, setNextActionOptions] = useState([])
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
   const dropdownRef = useRef(null);

  const [phaseValue, setPhaseValue] = useState({
    current_phase: "",
    next_action: "",
  })

  // ✅ Fetch suggestions
  const fetchContractSuggestions = (inputValue, fieldType) => {
    axios
      .get(
        `${BASE_URL}/get-datalisting?list=contract_to_close&search=${inputValue}&field=${fieldType}`,
        config
      )
      .then((res) => {
        const data = res?.data?.contract_to_close || {}

        const options = Object.entries(data).map(([key, value]) => ({
          value: key,
          label: value,
        }))

        if (fieldType === "current_phase") {
          setCurrentPhaseOptions(options)
        } else if (fieldType === "next_action") {
          setNextActionOptions(options)
        }
      })
      .catch((err) => {
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response?.data?.message)
        }
      })
  }

  // ✅ Initial load
  useEffect(() => {
    fetchContractSuggestions("", "current_phase")
    fetchContractSuggestions("", "next_action")
  }, [])

  // ✅ Sync UI with filterData
  useEffect(() => {
    if (filterData) {
      setPhaseValue({
        current_phase: filterData?.current_phase_label || "",
        next_action: filterData?.next_action_label || "",
      })
    }
  }, [filterData])

  // ✅ FIXED handleSelect (IMPORTANT)
  const handleSelect = (option, fieldName) => {
    if (!option) return;

    onSetFilterData((prev) => {
      if (!option.value) {
        const updated = { ...prev };
        delete updated[fieldName];
        delete updated[`${fieldName}_label`];
        return updated;
      }

      return {
        ...prev,
        [fieldName]: option.value,
        [`${fieldName}_label`]: option.label,
      };
    });

   // ✅ THIS LINE FIXES YOUR ISSUE
    if (onCallApiAgain) {
      onCallApiAgain(); // closes filter / refreshes data
    }

    // update UI
    setPhaseValue((prev) => ({
      ...prev,
      [fieldName]: option.label,
    }));
  };

  const fetchUsers = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/get-datalisting?list=users`, config)
      .then((res) => {
        const data = res?.data?.data;
        const options = data?.map((el) => ({ value: el?.id, label: `${el?.first_name} ${el?.last_name}`, role: el?.role_id }));
        setBrokerOptions(options?.filter((el) => el.role === 3));
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSidebarOpen) {
      fetchUsers()
    }
  }, [isSidebarOpen])

  const handleFilterChange = (value, name) => {
    onSetFilterData({ ...filterData, [name]: value });
  };

  // ✅ Archive mode
  if (from === "contractclosearchive") {
    return (
      <div>
        <p className="head-4 dark-H">Date Range</p>

        <OptionalOption
          activeTab="date"
          onSetLastDate={(value) =>
            onSetFilterData((prev) => ({ ...prev, count: value }))
          }
          date={filterData?.count}
          onSetDateRange1={(value) =>
            onSetFilterData((prev) => ({ ...prev, start_count: value }))
          }
          date2={filterData?.start_count}
          onSetDateRange2={(value) =>
            onSetFilterData((prev) => ({ ...prev, end_count: value }))
          }
          date3={filterData?.end_count}
          onClear={() => {
            onSetFilterData((prev) => ({
              ...prev,
              count: "",
              start_count: "",
              end_count: "",
            }))
          }}
        />
      </div>
    )
  }

  return (
    <>
      {/* Current Phase */}
      <div className="mt-4">
        <label className="dark-H head-4 mb-2">Current Phase</label>
        <SearchDropdown
          name="current_phase"
          text={phaseValue.current_phase}
          placeholder="Write Current Phase Here"
          options={currentPhaseOptions}
          fetchSuggestions={(val) =>
            fetchContractSuggestions(val, "current_phase")
          }
          onSetOptions={setCurrentPhaseOptions}
          handleSelect={(option) =>
            handleSelect(option, "current_phase")
          }
        />
      </div>

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

      {/* Next Action */}
      <div className="mt-4">
        <label className="dark-H head-4 mb-2">Next Action</label>
        <SearchDropdown
          name="next_action"
          text={phaseValue.next_action}
          placeholder="Write Next Action Here"
          options={nextActionOptions}
          fetchSuggestions={(val) =>
            fetchContractSuggestions(val, "next_action")
          }
          onSetOptions={setNextActionOptions}
          handleSelect={(option) =>
            handleSelect(option, "next_action")
          }
        />
      </div>

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

      <div>
        <p className="head-4 dark-H">Advisor</p>
        <div className="w-[100%] mt-2">
          <Select
            className="body-S"
            placeholder="Search here ..."
            value={brokerOptions?.find((el) => el?.value == filterData?.advisor) || null}
            options={brokerOptions}
            onChange={(option) => {
              handleFilterChange(option.value, "advisor");
            }}
          />
        </div>
      </div>

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

      <div className="mt-4">
        <p className="head-4 dark-H">Status</p>

        <div ref={dropdownRef} className="custom-dropdown mt-2">
          <div
            role="button"
            className="select-header-input capitalize light-bg-L body-N dark-M flex justify-between items-center"
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          >
            {filterData.status === ""
              ? "Select"
              : (ContractToCloseStatusOptions.find((el) => el.value === filterData.status)?.label || "Select")
            } <ArrowDown />
          </div>
          {isOpen && (
            <div className="dropdown-list-container light-bg-L dark-M body-N shadow rounded-box" style={{ width: "100%" }}>
              <ul className="dropdown-list">
                {ContractToCloseStatusOptions.map((el, i) => (
                  <li
                    key={i}
                    role="button"
                    onClick={() => {
                      handleFilterChange(el.value, "status");
                      setIsOpen(false);
                    }}
                    className={`${filterData.status === el.value ? "active" : ""}`}
                  >
                    {el.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

      </div>

      <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

      <div>
        <p className="head-4 dark-H capitalize">{from} Date Range :Last 90 Days</p>

        <OptionalOption
          activeTab="date"
          onSetLastDate={(value) => handleFilterChange(value, "created_at")}
          date={filterData?.created_at}
          onSetDateRange1={(value) => {
            handleFilterChange(value, "created_start_date");
          }}
          date2={filterData?.created_start_date}
          onSetDateRange2={(value) => handleFilterChange(value, "created_end_date")}
          date3={filterData?.created_end_date}
          onClear={() => {
            onSetFilterData({ ...filterData, created_at: "", created_start_date: "", created_end_date: "" });
          }}
        />
      </div>

    </>
  )
}

export default ContractCloseFilterData